import React from 'react';

import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import Layout from '../../../containers/Layout';
import AddOrEditPageTabs from '../../../components/AddOrEditPage/AddOrEditPageTabs';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';

import ImportService from '../../../services/ImportService';
import { useUiContext } from '../../../context/UiContext';
import usePagination from '../../../hooks/usePagination';

export default function ImportsPage() {
  const navigate = useNavigate();

  const { showDialogModal, showToast } = useUiContext();

  const {
    currentPage,
    switchToPage,
    pageTotal,
    isFetching,
    refetch,
    data: imports,
  } = usePagination({
    queryKey: ['imports'],
    queryFn: ImportService.getImports,
  });

  const { mutate: deleteImport } = useMutation({
    mutationFn: (importId) => ImportService.deleteImport(importId),
    onSuccess: () => {
      showToast({
        text: 'Import deleted successfully',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const tabs = [
    {
      label: 'Loans',
      onClick: () => {
        navigate('/loans');
      },
    },
    {
      label: 'Imports',
      onClick: () => {},
      active: true,
    },
  ];

  const cols = [
    {
      title: 'Status',
      keys: ['status'],
      format: (value) => value.toLowerCase(),
      link: (row) => `/loans/imports/${row.id}`,
    },
    {
      title: 'Lender',
      keys: ['_lender.name'],
    },
    {
      title: 'Loan number',
      keys: ['_loan.pixel_loan_number'],
    },
    {
      title: 'Property',
      keys: ['_loan._property.address_line_1'],
    },
    {
      title: '',
      type: 'delete',
      isVisible: (row) => !row?._import_documents?.length,
      onClick: (row) => {
        showDialogModal({
          title: 'Confiramtion',
          message: 'Are you sure you want to delete this import?',
          type: 'danger',
          onConfirm: () => deleteImport(row.id),
          confirmButtonLabel: 'Yes, delete it',
          cancelButtonLabel: 'No, cancel',
        });
      },
    },
  ];

  return (
    <Layout>
      <div className="flex">
        {/* Content area */}
        <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <main className="grow">
            <div className="px-4 sm:px-6 lg:px-8 py-8 pb-10 w-full max-w-9xl mx-auto">
              {/* Page header */}
              <div className="sm:flex sm:justify-between sm:items-center mb-8">
                {/* Left: Title */}
                <div className="mb-4 sm:mb-0">
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Imports
                  </h1>
                </div>

                {/* Right: Actions */}
                <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                  {/* Add vendor button */}
                  <Link
                    to="/loans/imports/new"
                    className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
                  >
                    <svg
                      className="w-4 h-4 fill-current opacity-50 shrink-0"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                    </svg>
                    <span className="hidden xs:block ml-2">Start Import</span>
                  </Link>
                </div>
              </div>
              <div className="mb-2 sm:w-60 relative z-0">
                <AddOrEditPageTabs tabs={tabs} />
              </div>
              <Table
                rows={imports?.items}
                cols={cols}
                isFetching={isFetching}
                title="Imports"
                minHeight={565}
              />
              <Pagination
                pageTotal={pageTotal}
                switchToPage={switchToPage}
                currentPage={currentPage}
              />
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
}
